<!--
 * App Layout
 * --------------------
 * Main layout component for the shell application.
 * Responsible for rendering the global structure of the app including:
 *
 *  - Application header
 *  - Left navigation (product-specific)
 *  - Micro-frontend (MFE) content outlet
 *  - Global SPA loader (store-driven)
 *
 * Responsibilities:
 *  1. Waits until all micro frontends signal readiness via `shellReady`.
 *  2. Dynamically loads the correct sidebar menu based on the current route.
 *     - Shows the menu only for product routes:
 *         • /accounts  → Marketing menu
 *         • /service   → Service menu
 *         • /commerce  → Commerce menu
 *  3. Reactively updates the menu when the route changes
 *     without reloading the application.
 *  4. Displays a global Vuetify loader during SPA route transitions
 *     and micro-frontend loading (controlled via Pinia store).
 *
 * Implementation details:
 *  - Uses `createMenuForRoute()` from `unifiedMenu.ts`.
 *  - Uses Vuetify `v-overlay` + `v-progress-circular`.
 *  - Compatible with Vue 3 + single-spa shell architecture.
 *
 * @author Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-30
 * @updated 2026-02-10
 -->

 <template>
  <div>
    <!-- ===============================================================
         GLOBAL HEADER
         =============================================================== -->
    <TheHeader />
    <!-- ===============================================================
         GLOBAL SPA LOADER
         =============================================================== -->
         <v-overlay
            :model-value="loading"
            class="spa-loader"
            persistent
            width="100%"
            height="100%"
            opacity="0.25"
          >
          <v-row justify="center" align="center" class="loader-row">
              <v-progress-circular
                indeterminate
                size="48"
                width="4"
                color="primary"
              />
            </v-row>
          </v-overlay>
    <!-- ===============================================================
         APPLICATION LAYOUT
         =============================================================== -->
    <v-main class="mt-16">
      <v-row no-gutters>
        <!-- -----------------------------------------------------------
             LEFT NAVIGATION
             Rendered only when product menu is available
        ------------------------------------------------------------ -->
        <v-col cols="auto">
          <TheUnifiedNavigation
            class="mt-16 content-area index"
            :account-id="userStore.getAccountId" 
            :nav-blocks="navigation" 
            :enableStoreMenu="saleNavigation"
            :collapsed="userStore.isNavigationRail"
            @update:collapsed="toggleLeftMenu"
          >
          <template #store-control>
            <div id="sale-channel-navigation"></div> 
          </template>
          </TheUnifiedNavigation>
        </v-col>

        <!-- -----------------------------------------------------------
             MAIN CONTENT AREA
        ------------------------------------------------------------ -->
        <v-col cols="12">
          <div class="content-area">
            <DrawerHost ref="drawerRef" />

            <router-view />
          </div>
        </v-col>
      </v-row>

      <AlertModal :show="alertModalState.show" :state="alertModalItems" @confirm="handleAlertConfirm"  @cancel="alertModalService.hide()" />
    </v-main>
  </div>
</template>

<script setup lang="ts">
/**
 * ============================================================================
 * Unified Shell – Navigation & Loader Controller
 * ============================================================================
 *
 * Behavior Summary
 * ----------------
 * - Displays unified navigation ONLY for supported product routes
 *   (Marketing, Service, Commerce).
 * - Navigation menu is loaded lazily and reacts to route changes.
 * - Global loader visibility is controlled via cross-MFE events.
 *
 * Communication Model
 * -------------------
 * - MFEs dispatch `MFE_LOADED` events to signal loading state.
 * - Shell listens and updates `loading` accordingly.
 * - MFEs can also request navigation rail changes via custom events.
 *
 * Design Notes
 * ------------
 * - Shell remains framework-agnostic.
 * - MFEs do not directly import shell dependencies.
 * - All UI side-effects are centralized here.
 * ============================================================================
 */

import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { navigateToUrl } from "single-spa";

/* ---------------------------------------------------------------------------
 * UI Components
 * ---------------------------------------------------------------------------
 */
import { TheHeader, DrawerHost } from "../components";
import {
  TheUnifiedNavigation,
  AlertModal,
  GenericModalState,
} from "@maropost-ui/liquidsky-ui";

/* ---------------------------------------------------------------------------
 * Stores & Services
 * ---------------------------------------------------------------------------
 */
import { useUserStore } from "../stores";
import { useNavigation } from "../composable/sidemenu/unifiedNavigation";
import { alertModalService } from "@/lib/services/alertModalService";
import { useHubSpotChat } from "@/lib/plugins/hubspotChat";

/* ============================================================================
 * STORES & ROUTER
 * ============================================================================
 */

// User / permissions store
const userStore = useUserStore();

// Vue Router instance
const router = useRouter();

// Global alert modal reactive state
const alertModalState = alertModalService.getState();

// Global loader state (controlled by MFEs)
const loading = ref(false);

const hubSpotChat = useHubSpotChat();

/**
 * Load HubSpot chat after the shell loader is idle (initial mount or MFE finished).
 */
const loadHubSpotWhenIdle = (): void => {
  if (!loading.value) {
    hubSpotChat.loadIfEnabled();
  }
};

onMounted(loadHubSpotWhenIdle);

watch(loading, loadHubSpotWhenIdle, { flush: "post" });

// Sales navigation visibility (permission-based)
const saleNavigation = computed(() => userStore.getSaleNavigation);

// Normalized alert modal props
const alertModalItems = computed<GenericModalState>(() => ({
  title: alertModalState.title,
  subtitle: alertModalState.subtitle,
  description: alertModalState.description,
}));

/* ============================================================================
 * NAVIGATION STATE
 * ============================================================================
 */

/**
 * Unified navigation configuration
 * - Generated dynamically based on user permissions
 * - Passed directly to <TheUnifiedNavigation />
 */
const { navigation } = useNavigation();

/* ============================================================================
 * CROSS-MFE EVENT HANDLERS
 * ============================================================================
 */

/**
 * Handle MFE loader lifecycle events
 *
 * Event: MFE_LOADED
 * Payload:
 *   - true  → show loader
 *   - false → hide loader
 *
 * Dispatched by:
 *   - Shell (before mounting/unmounting MFEs)
 *   - Individual MFEs (when fully initialized)
 */
const onMfeLoaded = (event: Event) => {
  const customEvent = event as CustomEvent<boolean>;
  loading.value = customEvent.detail;
};

// Register loader event listener
window.addEventListener("MFE_LOADED", onMfeLoaded);

/**
 * Handle navigation rail toggle requests from MFEs
 *
 * Event: mfe:folder-open
 * Payload:
 *   - true  → open navigation rail
 *   - false → close navigation rail
 *
 * Used when MFEs want to temporarily control layout
 * (e.g. opening a folder or deep view)
 */
window.addEventListener("mfe:folder-open", (event) => {
  const customEvent = event as CustomEvent<boolean>;
  if (customEvent.detail !== undefined) {
    userStore.setNavigationRail(customEvent.detail);
  }
});

/* ============================================================================
 * UI ACTIONS
 * ============================================================================
 */

/**
 * Toggle left navigation drawer manually
 * (Triggered by header / hamburger icon)
 */
const toggleLeftMenu = (isDrawerClosed: boolean): void => {
  userStore.setNavigationRail(isDrawerClosed);
};

/**
 * Handle alert modal confirmation
 *
 * Scenario:
 * - User hits usage limit for a feature
 * - Redirect them to Account & Billing page
 *
 * Routing Logic:
 * - Marketing users → SPA navigation
 * - Non-marketing users → full page redirect
 */
const handleAlertConfirm = () => {
  const accountId = userStore.getAccountId;
  const path = `/accounts/${accountId}/account-&-billing`;

  if (
    !userStore.getProductAccess("marketing") ||
    !userStore.getProductAccessOnRoles("marketing")
  ) {
    router.push(path);
  } else {
    navigateToUrl(path);
  }

  alertModalService.hide();
};
</script>


<style>
/* ============================================================================
   GLOBAL LOADER
============================================================================ */

.loader-row {
  height: 100%;
}

/* ============================================================================
   LAYOUT
============================================================================ */

.content-area {
  height: calc(100vh - 64px) !important;
  overflow: auto;
  background: #fafafa;
}
.index {
  z-index: 10 !important;
}
</style>
